import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faCheck, faFire, faUtensils, faClock } from '@fortawesome/free-solid-svg-icons';
import Sidebar from '../Sidebar/Sidebar';
import { useSocket } from '../../context/SocketContext';
import { message as antMessage } from 'antd';
import { buildApiUrl } from '../../config/api.config';

const KitchenDisplay = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const socket = useSocket();

  const fetchOrders = async () => {
    try {
      setRefreshing(true);
      setError(null);
      let restaurantId = localStorage.getItem('currentRestaurantId');
      
      // Safe parsing of user details
      let userDetails = {};
      try {
          const storedUser = localStorage.getItem('userDetails');
          if (storedUser) userDetails = JSON.parse(storedUser);
      } catch (e) {
          console.error("Failed to parse user details");
      }

      // Force extraction if it's an object or malformed string
      if (restaurantId && (restaurantId.includes('[object') || restaurantId.startsWith('{'))) {
          try {
              if (restaurantId.startsWith('{')) {
                  const parsed = JSON.parse(restaurantId);
                  restaurantId = parsed._id || parsed.id || restaurantId;
              } else {
                  // If it's the literal string "[object Object]", fallback to userDetails
                  restaurantId = null; 
              }
          } catch(e) {
              restaurantId = null;
          }
      }

      // Fallback: Try getting from userDetails if not explicitly selected or if malformed
      if (!restaurantId || restaurantId === '[object Object]') {
          if (userDetails?.restaurantId) {
              restaurantId = typeof userDetails.restaurantId === 'object' 
                  ? userDetails.restaurantId._id 
                  : userDetails.restaurantId;
              
              if (restaurantId) localStorage.setItem('currentRestaurantId', restaurantId);
          }
      }


      if (!restaurantId) {
        setError("Restaurant context not found. Please try switching your restaurant or logging in again.");
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const response = await axios.post(buildApiUrl('/kitchen/active'), {
        restaurantId
      });

      setOrders(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching kitchen orders:", err);
      setError(`Failed to load orders: ${err.message}. Check console for details.`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };


  useEffect(() => {
    fetchOrders();
    
    if (socket) {
      const restaurantId = localStorage.getItem('currentRestaurantId');
      if (restaurantId) {
        socket.emit('joinRoom', `kitchenOrders_${restaurantId}`);
        
        socket.on('newOrder', (data) => {
          antMessage.info('New Order Received!', 2);
          fetchOrders();
        });

        socket.on('orderUpdated', (data) => {
            // Partial update for better performance
            setOrders(prevOrders => prevOrders.map(order => {
                if (order._id === data.invoiceId) {
                    return {
                        ...order,
                        foods: order.foods.map(food => 
                            food._id === data.itemId ? { ...food, kitchenStatus: data.status } : food
                        )
                    };
                }
                return order;
            }));
        });
      }
    }

    const interval = setInterval(fetchOrders, 60000); // Auto-refresh fallback every 60s
    return () => {
        clearInterval(interval);
        if (socket) {
            socket.off('newOrder');
            socket.off('orderUpdated');
        }
    };
  }, [socket]);

  const updateStatus = async (invoiceId, itemId, newStatus) => {
    try {
      // Optimistic update
      setOrders(prevOrders => prevOrders.map(order => {
        if (order._id === invoiceId) {
          return {
            ...order,
            foods: order.foods.map(food => 
              food._id === itemId ? { ...food, kitchenStatus: newStatus } : food
            )
          };
        }
        return order;
      }));

      await axios.post(buildApiUrl('/kitchen/status'), {
        invoiceId,
        itemId,
        status: newStatus
      });
    } catch (err) {
      console.error("Error updating status:", err);
      // Revert on error would be ideal, but simple fetch refresh works for now
      fetchOrders();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ff4135'; // Brand Red
      case 'preparing': return '#ffd700'; // Gold
      case 'ready': return '#00d1b2'; // Teal
      default: return '#ccc';
    }
  };

  const getNextStatus = (current) => {
    if (current === 'pending') return 'preparing';
    if (current === 'preparing') return 'ready';
    if (current === 'ready') return 'served';
    return 'served';
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#1B1C1F', color: 'white' }}>
        <Sidebar aria-label="Sidebar" />
        <div style={{ flex: 1, padding: '2rem', overflowY: 'auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div>
                    <h1 style={{ fontSize: '2rem', fontWeight: '800', margin: 0, display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <FontAwesomeIcon icon={faFire} style={{ color: 'white' }} />
                        Kitchen Display System
                    </h1>
                    <p style={{ color: 'rgba(255,255,255,0.5)', margin: '0.5rem 0 0 0' }}>
                        Real-time order management
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>
                        <FontAwesomeIcon icon={faClock} style={{ marginRight: '0.5rem' }} />
                        Auto-refreshing
                    </div>
                    <button 
                        onClick={fetchOrders} 
                        disabled={refreshing}
                        style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', width: '40px', height: '40px', borderRadius: '50%', cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                        <FontAwesomeIcon icon={faRotate} spin={refreshing} />
                    </button>
                </div>
            </div>


            {error && <div style={{ padding: '1rem', background: 'rgba(255,0,0,0.1)', color: '#ff4135', borderRadius: '8px', marginBottom: '1rem' }}>{error}</div>}

            {loading && !orders.length ? (
                <div style={{ textAlign: 'center', padding: '5rem', color: 'rgba(255,255,255,0.3)' }}>Loading active orders...</div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                    {orders.length === 0 ? (
                         <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '5rem', background: '#1B1C1F', borderRadius: '16px', border: '1px dashed rgba(255,255,255,0.1)' }}>
                            <FontAwesomeIcon icon={faUtensils} style={{ fontSize: '3rem', color: 'rgba(255,255,255,0.1)', marginBottom: '1rem' }} />
                            <h3 style={{ margin: 0, color: 'rgba(255,255,255,0.5)' }}>All caught up!</h3>
                            <p style={{ color: 'rgba(255,255,255,0.3)' }}>No pending orders in the kitchen.</p>
                         </div>
                    ) : (
                        orders.map(order => (
                            <div key={order._id} style={{ background: '#212226', borderRadius: '16px', padding: '1.5rem', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column' }}>
                                {/* Order Header */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                    <div>
                                        <div style={{ fontSize: '1.2rem', fontWeight: '800', color: 'white' }}>
                                            {order.reservedTableInfo ? `Table ${order.reservedTableInfo.tableNumber}` : `INV #${order.invoiceNumber}`}
                                        </div>
                                        <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.2rem' }}>
                                            {formatTime(order.createdAt)} • {order.foods.length} items
                                        </div>
                                    </div>
                                    <div style={{ padding: '0.4rem 0.8rem', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', color: 'rgba(255,255,255,0.6)' }}>
                                        {Math.floor((new Date() - new Date(order.createdAt)) / 60000)}m ago
                                    </div>
                                </div>

                                {/* Items List */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                                    {order.foods.filter(f => f.kitchenStatus !== 'served').map((item, idx) => (
                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.2)', padding: '0.8rem', borderRadius: '8px' }}>
                                            <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center' }}>
                                                <div style={{ width: '24px', height: '24px', background: 'rgba(255,255,255,0.1)', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', fontWeight: 'bold' }}>
                                                    {item.quantity}
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                     <span style={{ fontWeight: '600', fontSize: '0.95rem' }}>{item.name}</span>
                                                     <span style={{ fontSize: '0.75rem', color: getStatusColor(item.kitchenStatus), fontWeight: 'bold', textTransform: 'uppercase' }}>{item.kitchenStatus}</span>
                                                </div>
                                            </div>
                                            
                                            <button
                                                onClick={() => updateStatus(order._id, item._id, getNextStatus(item.kitchenStatus))}
                                                style={{ 
                                                    background: getStatusColor(item.kitchenStatus), 
                                                    color: item.kitchenStatus === 'preparing' ? 'black' : 'white',
                                                    border: 'none', 
                                                    borderRadius: '6px', 
                                                    padding: '0.5rem 1rem', 
                                                    fontWeight: '700', 
                                                    cursor: 'pointer',
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                {item.kitchenStatus === 'ready' ? <FontAwesomeIcon icon={faCheck} /> : 'Next'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    </div>
  );
};

export default KitchenDisplay;
